import jsPDF from 'jspdf';
import {
  scoreSmartGoal,
  computeProgress,
  goalHealth,
  daysUntil,
  parseNumber,
  SMART_CRITERIA
} from './smartGoals';
import { getSmartGoalGuide } from '../data/smartGoalGuide';
import { createKit, PALETTE } from './pdfKit';

/*
 * The SMART goal report.
 *
 * Colour has two jobs and never mixes them: one indigo ramp carries every
 * magnitude (rings, meters, bars, the radar, the trend), and the fixed status
 * palette carries state as a mark beside an ink label — never as the label,
 * since amber measures 1.83:1 on white. Card tints are decoration only.
 */

const sanitize = (value) => {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/[‘’‛]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—]/g, '-')
    .replace(/…/g, '...')
    .replace(/→/g, '->')
    .replace(/ /g, ' ');
};

const PAGE = { w: 210, h: 297, margin: 15, bottom: 20 };
const CONTENT = PAGE.w - PAGE.margin * 2;
const RIGHT = PAGE.w - PAGE.margin;

const HEALTH = {
  'not-started': { label: 'Not started', mark: PALETTE.status.idle, icon: 'clock' },
  'on-track': { label: 'On track', mark: PALETTE.status.good, icon: 'trend' },
  'at-risk': { label: 'At risk', mark: PALETTE.status.warning, icon: 'alert' },
  completed: { label: 'Completed', mark: PALETTE.status.good, icon: 'check' }
};

const BAND = {
  excellent: { label: 'Ready to commit', mark: PALETTE.status.good },
  solid: { label: 'Solid draft', mark: PALETTE.status.good },
  draft: { label: 'Needs work', mark: PALETTE.status.warning },
  vague: { label: 'Still vague', mark: PALETTE.status.critical }
};

const bandOf = (score) => {
  if (score >= 85) return BAND.excellent;
  if (score >= 70) return BAND.solid;
  if (score >= 45) return BAND.draft;
  return BAND.vague;
};

const dayOf = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const elapsedFraction = (goal) => {
  const start = dayOf(goal.time_bound?.start_date);
  const due = dayOf(goal.time_bound?.due_date);
  if (!start || !due || due <= start) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.max(0, Math.min(1, (now - start) / (due - start)));
};

const prettyDate = (value) => {
  const date = dayOf(value);
  if (!date) return value || '—';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const exportSmartGoalsToPDF = (goals = [], options = {}) => {
  const {
    projectName = '',
    title,
    includeGuide = true,
    language = 'en',
    fileName,
    returnDoc = false
  } = options;

  const single = goals.length === 1 ? goals[0] : null;
  const docTitle = title || (single ? single.title || 'SMART goal' : 'SMART Goals');

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  doc.setProperties({
    title: sanitize(docTitle),
    subject: projectName ? `SMART goal report — ${sanitize(projectName)}` : 'SMART goal report',
    creator: 'Strategic Roadmap'
  });

  const kit = createKit(doc);
  const { setInk, setFill, setStroke, card, iconBadge, chip, ring, meter, radar, areaChart, table, icons } = kit;

  let y = PAGE.margin;
  let page = 1;

  // ── page furniture ────────────────────────────────────────────────────────

  const footer = () => {
    setStroke(PALETTE.hair);
    doc.setLineWidth(0.25);
    doc.setLineCap('butt');
    doc.line(PAGE.margin, PAGE.h - 14, RIGHT, PAGE.h - 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.4);
    setInk(PALETTE.muted);
    const left = [sanitize(docTitle), sanitize(projectName)].filter(Boolean).join('   ·   ');
    doc.text(left, PAGE.margin, PAGE.h - 9.5, { maxWidth: CONTENT - 16 });
    setFill(PALETTE.ramp.wash);
    doc.circle(RIGHT - 2.6, PAGE.h - 10.2, 3.1, 'F');
    setInk(PALETTE.ramp.deep);
    doc.setFont('helvetica', 'bold');
    doc.text(String(page), RIGHT - 2.6, PAGE.h - 9.2, { align: 'center' });
    page += 1;
  };

  const newPage = () => {
    footer();
    doc.addPage();
    y = PAGE.margin;
  };

  const space = (needed) => {
    if (y + needed > PAGE.h - PAGE.bottom) newPage();
  };

  const paragraph = (value, opts = {}) => {
    const {
      x = PAGE.margin, width = CONTENT, size = 9.6, style = 'normal',
      color = PALETTE.body, leading = 5.2, gap = 0
    } = opts;
    if (!value) return;
    doc.setFont('helvetica', style);
    doc.setFontSize(size);
    setInk(color);
    doc.splitTextToSize(sanitize(value), width).forEach((line) => {
      space(leading + 2);
      doc.text(line, x, y);
      y += leading;
    });
    y += gap;
  };

  /** Measure wrapped text without drawing it, so cards can size themselves. */
  const measure = (value, width, size, style = 'normal') => {
    doc.setFont('helvetica', style);
    doc.setFontSize(size);
    return doc.splitTextToSize(sanitize(value || ''), width).length;
  };

  const sectionHeader = (label, icon, needs = 0) => {
    // Keep the header with its content: reserve both, not just the header.
    space(18 + needs);
    y += 3;
    iconBadge(icon, PAGE.margin, y - 3.4, { radius: 3.6, size: 4 });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    setInk(PALETTE.ink);
    doc.text(sanitize(label), PAGE.margin + 10, y + 1.2);
    setStroke(PALETTE.hair);
    doc.setLineWidth(0.25);
    doc.setLineCap('butt');
    const labelWidth = doc.getTextWidth(sanitize(label));
    doc.line(PAGE.margin + 13 + labelWidth, y, RIGHT, y);
    y += 9;
  };

  // ── the hero card ─────────────────────────────────────────────────────────

  const heroCard = (goal) => {
    const progress = computeProgress(goal);
    const health = HEALTH[goalHealth(goal)] || HEALTH['not-started'];
    const titleWidth = CONTENT - 62;
    const titleLines = Math.min(3, measure(goal.title || 'Untitled goal', titleWidth, 19, 'bold'));
    const height = Math.max(56, 40 + titleLines * 8);

    card(PAGE.margin, y, CONTENT, height, { fill: PALETTE.tint.indigo, radius: 4.5 });

    const innerX = PAGE.margin + 9;
    let innerY = y + 11;

    icons.target(innerX, innerY - 3.2, 4, PALETTE.ramp.fill);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    setInk(PALETTE.ramp.deep);
    doc.text(
      sanitize(['SMART GOAL', goal.category ? goal.category.toUpperCase() : null].filter(Boolean).join('   ·   ')),
      innerX + 6, innerY, { charSpace: 0.4 }
    );
    innerY += 8;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(19);
    setInk(PALETTE.ink);
    doc.splitTextToSize(sanitize(goal.title || 'Untitled goal'), titleWidth)
      .slice(0, 3)
      .forEach((line) => {
        doc.text(line, innerX, innerY);
        innerY += 8;
      });

    innerY += 1.5;
    let chipX = innerX;
    chipX += chip(health.label, chipX, innerY, { mark: health.mark, tint: PALETTE.white }) + 2.5;
    if (goal.priority) {
      chipX += chip(`${goal.priority} priority`, chipX, innerY, { icon: 'flag', tint: PALETTE.white }) + 2.5;
    }
    if (goal.owner) chip(goal.owner, chipX, innerY, { icon: 'user', tint: PALETTE.white });

    // Radial meter, right-hand side.
    ring(RIGHT - 31, y + height / 2, 18, progress, {
      weight: 6,
      track: PALETTE.white,
      value: `${progress}%`,
      caption: 'to target',
      valueSize: 18
    });

    y += height + 6;
  };

  // ── the goal, set to be read ──────────────────────────────────────────────

  const statementCard = (goal) => {
    if (!goal.specific) return;
    const textWidth = CONTENT - 22;
    const lines = measure(goal.specific, textWidth, 11, 'normal');
    const height = lines * 6.1 + 13;
    space(height + 4);

    card(PAGE.margin, y, CONTENT, height, {
      fill: PALETTE.white, radius: 4, accent: PALETTE.ramp.fill, border: PALETTE.hair
    });
    icons.quote(PAGE.margin + 7, y + 6.5, 5, PALETTE.ramp.track);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    setInk(PALETTE.ink);
    let textY = y + 11;
    doc.splitTextToSize(sanitize(goal.specific), textWidth).forEach((line) => {
      doc.text(line, PAGE.margin + 14, textY);
      textY += 6.1;
    });

    y += height + 7;
  };

  // ── four facts, as tinted stat tiles ──────────────────────────────────────

  const statRow = (goal) => {
    const measurable = goal.measurable || {};
    const score = scoreSmartGoal(goal).total;
    const remaining = daysUntil(goal.time_bound?.due_date);
    const unit = measurable.unit ? ` ${measurable.unit}` : '';

    const tiles = [
      {
        icon: 'trend', tint: PALETTE.tint.mint, color: [16, 122, 76],
        label: 'Now', value: `${measurable.current || measurable.baseline || '—'}${unit}`
      },
      {
        icon: 'target', tint: PALETTE.tint.indigo, color: PALETTE.ramp.fill,
        label: 'Target', value: `${measurable.target || '—'}${unit}`
      },
      {
        icon: 'clock', tint: PALETTE.tint.peach, color: [180, 83, 9],
        label: remaining !== null && remaining < 0 ? 'Overdue by' : 'Time left',
        value: remaining === null ? '—' : `${Math.abs(remaining)} days`
      },
      {
        icon: 'star', tint: PALETTE.tint.lilac, color: [126, 34, 206],
        label: 'SMART score', value: `${score}`
      }
    ];

    space(28);
    const gap = 3;
    const tileW = (CONTENT - gap * 3) / 4;
    tiles.forEach((tile, i) => {
      const x = PAGE.margin + i * (tileW + gap);
      card(x, y, tileW, 23, { fill: tile.tint, radius: 3.5 });
      iconBadge(tile.icon, x + 5, y + 4.5, {
        radius: 3.4, size: 3.8, tint: PALETTE.white, color: tile.color
      });
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.4);
      setInk(PALETTE.muted);
      doc.text(tile.label.toUpperCase(), x + 14, y + 8.2, { charSpace: 0.3 });
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      setInk(PALETTE.ink);
      doc.text(sanitize(tile.value), x + 5, y + 18, { maxWidth: tileW - 9 });
    });
    y += 23 + 8;
  };

  // ── the journey from baseline to target ───────────────────────────────────

  const journeyBlock = (goal) => {
    const measurable = goal.measurable || {};
    if (!measurable.metric && !measurable.target) return;

    sectionHeader(measurable.metric || 'Progress', 'bars', 34);

    const progress = computeProgress(goal);
    const elapsed = elapsedFraction(goal);
    const unit = measurable.unit ? ` ${measurable.unit}` : '';

    const stops = [
      { label: 'Baseline', value: `${measurable.baseline || '—'}${unit}`, done: true },
      { label: 'Today', value: `${measurable.current || measurable.baseline || '—'}${unit}`, done: true },
      { label: 'Target', value: `${measurable.target || '—'}${unit}`, done: false }
    ];
    const stopW = CONTENT / 3;
    stops.forEach((stop, i) => {
      const x = PAGE.margin + i * stopW;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.4);
      setInk(PALETTE.muted);
      doc.text(stop.label.toUpperCase(), x, y, { charSpace: 0.3 });
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(i === 1 ? 15 : 12);
      setInk(i === 1 ? PALETTE.ink : PALETTE.body);
      doc.text(sanitize(stop.value), x, y + 7);
    });
    y += 12;

    meter(PAGE.margin, y, CONTENT, progress, { height: 5 });

    // How much of the calendar has gone — the comparison that matters.
    if (elapsed !== null && elapsed > 0.02 && elapsed < 0.99) {
      const markX = PAGE.margin + CONTENT * elapsed;
      setStroke(PALETTE.body);
      doc.setLineWidth(0.5);
      doc.setLineCap('round');
      doc.line(markX, y - 2, markX, y + 7);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.4);
      setInk(PALETTE.muted);
      const flip = elapsed > 0.72;
      doc.text(`${Math.round(elapsed * 100)}% of the time gone`,
        flip ? markX - 2 : markX + 2, y + 11, { align: flip ? 'right' : 'left' });
    }
    y += 16;
  };

  // ── SMART score: radar plus the numbers ───────────────────────────────────

  const scoreBlock = (goal) => {
    const score = scoreSmartGoal(goal);
    const band = bandOf(score.total);

    sectionHeader('SMART score', 'star', 58);

    const blockTop = y;
    const radarCx = PAGE.margin + 34;
    const radarCy = blockTop + 27;

    radar(radarCx, radarCy, 22, SMART_CRITERIA.map((criterion) => ({
      letter: criterion.letter,
      value: score.breakdown[criterion.key] || 0
    })));

    // The same numbers, spelled out — the shape is never the only channel.
    const listX = PAGE.margin + 74;
    const listW = RIGHT - listX;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(26);
    setInk(PALETTE.ink);
    doc.text(String(score.total), listX, blockTop + 9);
    const scoreW = doc.getTextWidth(String(score.total));
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    setInk(PALETTE.muted);
    doc.text('/ 100', listX + scoreW + 2, blockTop + 9);
    chip(band.label, listX + scoreW + 20, blockTop + 3.5, { mark: band.mark, tint: PALETTE.tint.slate });

    SMART_CRITERIA.forEach((criterion, i) => {
      const rowY = blockTop + 16 + i * 7.4;
      const value = score.breakdown[criterion.key] || 0;
      setFill(PALETTE.ramp.wash);
      doc.circle(listX + 2.4, rowY + 1.2, 2.4, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      setInk(PALETTE.ramp.deep);
      doc.text(criterion.letter, listX + 2.4, rowY + 2.3, { align: 'center' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.6);
      setInk(PALETTE.body);
      const name = criterion.key === 'timeBound'
        ? 'Time-bound'
        : criterion.key.charAt(0).toUpperCase() + criterion.key.slice(1);
      doc.text(name, listX + 7, rowY + 2.2);

      meter(listX + 34, rowY - 0.2, listW - 44, value, { height: 2.8 });
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.6);
      setInk(PALETTE.ink);
      doc.text(String(value), RIGHT, rowY + 2.2, { align: 'right' });
    });

    y = blockTop + 56;
  };

  // ── the case for it ───────────────────────────────────────────────────────

  const caseBlock = (goal) => {
    const cards = [
      { icon: 'bulb', tint: PALETTE.tint.mint, color: [16, 122, 76], title: 'Why this is achievable', body: goal.achievable },
      { icon: 'compass', tint: PALETTE.tint.lilac, color: [126, 34, 206], title: 'Why it matters now', body: goal.relevant }
    ].filter((entry) => entry.body);
    if (cards.length === 0) return;

    const gap = 4;
    const cardW = cards.length === 2 ? (CONTENT - gap) / 2 : CONTENT;
    const textW = cardW - 14;
    const height = Math.max(...cards.map((entry) => measure(entry.body, textW, 9.4) * 5 + 20));
    sectionHeader('The case for it', 'bulb', height + 4);

    cards.forEach((entry, i) => {
      const x = PAGE.margin + i * (cardW + gap);
      card(x, y, cardW, height, { fill: entry.tint, radius: 3.5 });
      iconBadge(entry.icon, x + 6, y + 6, { radius: 3.6, size: 4, tint: PALETTE.white, color: entry.color });
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      setInk(PALETTE.ink);
      doc.text(entry.title, x + 15, y + 10.4);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.4);
      setInk(PALETTE.body);
      let textY = y + 18;
      doc.splitTextToSize(sanitize(entry.body), textW).forEach((line) => {
        doc.text(line, x + 7, textY);
        textY += 5;
      });
    });

    y += height + 8;
  };

  // ── milestones ────────────────────────────────────────────────────────────

  const milestoneBlock = (goal) => {
    const milestones = (goal.milestones || []).filter((m) => m.title || m.date);
    if (milestones.length === 0) return;

    sectionHeader('Milestones', 'milestone', 40);

    const start = dayOf(goal.time_bound?.start_date);
    const due = dayOf(goal.time_bound?.due_date);
    const dated = milestones.filter((m) => dayOf(m.date));

    if (start && due && due > start && dated.length > 0) {
      space(26);
      const axisY = y + 7;
      const axisX = PAGE.margin + 3;
      const axisW = CONTENT - 6;
      const at = (date) => axisX + axisW * Math.max(0, Math.min(1, (date - start) / (due - start)));

      setStroke(PALETTE.ramp.track);
      doc.setLineWidth(1.6);
      doc.setLineCap('round');
      doc.line(axisX, axisY, axisX + axisW, axisY);

      const elapsed = elapsedFraction(goal);
      if (elapsed !== null && elapsed > 0 && elapsed < 1) {
        setStroke(PALETTE.ramp.fill);
        doc.setLineWidth(1.6);
        doc.line(axisX, axisY, axisX + axisW * elapsed, axisY);
      }

      dated.forEach((milestone) => {
        const x = at(dayOf(milestone.date));
        setFill(PALETTE.white);
        doc.circle(x, axisY, 2.6, 'F');
        if (milestone.completed) {
          setFill(PALETTE.ramp.fill);
          doc.circle(x, axisY, 2.1, 'F');
          icons.check(x - 1.4, axisY - 1.4, 2.8, PALETTE.white);
        } else {
          setStroke(PALETTE.ramp.mid);
          doc.setLineWidth(0.5);
          doc.circle(x, axisY, 2.1, 'S');
        }
      });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.4);
      setInk(PALETTE.muted);
      doc.text(prettyDate(goal.time_bound.start_date), axisX - 1, axisY + 7);
      doc.text(prettyDate(goal.time_bound.due_date), axisX + axisW + 1, axisY + 7, { align: 'right' });
      y = axisY + 12;
    }

    space(20);
    y = table(PAGE.margin + 2, y, CONTENT - 4, [
      {
        key: 'state', label: '', width: 8,
        render: (value, x, rowY) => {
          if (value) {
            setFill(PALETTE.status.good);
            doc.circle(x + 2.2, rowY + 2.8, 2.1, 'F');
            icons.check(x + 0.9, rowY + 1.5, 2.6, PALETTE.white);
          } else {
            setStroke(PALETTE.ramp.mid);
            doc.setLineWidth(0.45);
            doc.circle(x + 2.2, rowY + 2.8, 2.1, 'S');
          }
        }
      },
      { key: 'title', label: 'Milestone', width: CONTENT - 4 - 8 - 34, bold: true },
      { key: 'date', label: 'Date', width: 34, align: 'right' }
    ], milestones.map((milestone) => ({
      state: !!milestone.completed,
      title: milestone.title || 'Milestone',
      date: prettyDate(milestone.date)
    })), {
      ensure: (needed, cursor) => { y = cursor; space(needed); return y; }
    });

    y += 6;
  };

  // ── obstacles ─────────────────────────────────────────────────────────────

  const obstacleBlock = (goal) => {
    const obstacles = (goal.obstacles || []).filter((o) => o.risk || o.plan);
    if (obstacles.length === 0) return;

    const firstHeight = 12 + (obstacles[0].plan ? measure(obstacles[0].plan, CONTENT - 26, 9) : 0) * 4.8;
    sectionHeader('Obstacles and if-then plans', 'alert', firstHeight + 4);

    obstacles.forEach((obstacle) => {
      const textW = CONTENT - 26;
      const planLines = obstacle.plan ? measure(obstacle.plan, textW, 9) : 0;
      const height = 12 + planLines * 4.8;
      space(height + 5);

      card(PAGE.margin, y, CONTENT, height, { fill: PALETTE.tint.peach, radius: 3.5 });
      iconBadge('alert', PAGE.margin + 5, y + 4.5, {
        radius: 3.6, size: 4, tint: PALETTE.white, color: PALETTE.status.warning
      });

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9.2);
      setInk(PALETTE.ink);
      doc.text(sanitize(obstacle.risk || 'Risk'), PAGE.margin + 14, y + 8, { maxWidth: textW });

      if (obstacle.plan) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        setInk(PALETTE.body);
        let textY = y + 14.5;
        doc.splitTextToSize(sanitize(obstacle.plan), textW).forEach((line) => {
          doc.text(line, PAGE.margin + 14, textY);
          textY += 4.8;
        });
      }
      y += height + 4;
    });
    y += 3;
  };

  // ── check-ins ─────────────────────────────────────────────────────────────

  const checkinBlock = (goal) => {
    const checkins = [...(goal.checkins || [])]
      .filter((entry) => entry.date)
      .sort((a, b) => a.date.localeCompare(b.date));
    if (checkins.length === 0) return;

    // Reserve the plot and the first rows of the log so they never split.
    const points = checkins
      .map((entry) => ({ ...entry, value: parseNumber(entry.value), t: dayOf(entry.date)?.getTime() }))
      .filter((entry) => entry.value !== null && entry.t);
    const target = parseNumber(goal.measurable?.target);

    // Reserve the plot and the first rows of the log so they never split.
    sectionHeader('Check-ins', 'bars', points.length >= 2 ? 68 : 24);

    if (points.length >= 2) {
      const plotW = CONTENT - 20;
      areaChart(PAGE.margin, y, plotW, 30, points, {
        reference: target,
        referenceLabel: target !== null ? `target ${goal.measurable.target}` : undefined,
        endLabel: sanitize(checkins[checkins.length - 1].value)
      });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.4);
      setInk(PALETTE.muted);
      doc.text(prettyDate(points[0].date), PAGE.margin, y + 35);
      doc.text(prettyDate(points[points.length - 1].date), PAGE.margin + plotW, y + 35, { align: 'right' });
      y += 42;
    }

    const rows = [...checkins].reverse();
    space(22);
    y = table(PAGE.margin + 2, y, CONTENT - 4, [
      { key: 'date', label: 'Date', width: 34 },
      { key: 'value', label: 'Value', width: 24, bold: true },
      { key: 'note', label: 'Note', width: CONTENT - 4 - 58 }
    ], rows.map((entry) => ({
      date: prettyDate(entry.date),
      value: entry.value || '—',
      note: entry.note || ''
    })), {
      ensure: (needed, cursor) => { y = cursor; space(needed); return y; }
    });
    y += 6;
  };

  // ── a whole goal ──────────────────────────────────────────────────────────

  const goalSection = (goal) => {
    heroCard(goal);
    statementCard(goal);
    statRow(goal);
    journeyBlock(goal);
    scoreBlock(goal);
    caseBlock(goal);
    milestoneBlock(goal);
    obstacleBlock(goal);
    checkinBlock(goal);
  };

  // ── portfolio summary ─────────────────────────────────────────────────────

  const portfolioCover = () => {
    const scored = goals.map((goal) => ({
      goal,
      score: scoreSmartGoal(goal).total,
      progress: computeProgress(goal),
      health: goalHealth(goal)
    }));
    const mean = (list) => (list.length ? Math.round(list.reduce((a, b) => a + b, 0) / list.length) : 0);
    const avgProgress = mean(scored.map((s) => s.progress));

    card(PAGE.margin, y, CONTENT, 50, { fill: PALETTE.tint.indigo, radius: 4.5 });
    icons.target(PAGE.margin + 9, y + 8, 4, PALETTE.ramp.fill);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    setInk(PALETTE.ramp.deep);
    doc.text(sanitize(projectName ? projectName.toUpperCase() : 'SMART GOALS'),
      PAGE.margin + 15, y + 11.2, { charSpace: 0.4 });
    doc.setFontSize(19);
    setInk(PALETTE.ink);
    doc.text('Goal portfolio', PAGE.margin + 9, y + 25);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.4);
    setInk(PALETTE.body);
    doc.text(`${goals.length} goals · ${mean(scored.map((s) => s.score))} average SMART score`,
      PAGE.margin + 9, y + 33);
    let chipX = PAGE.margin + 9;
    chipX += chip(`${scored.filter((s) => s.health === 'on-track').length} on track`, chipX, y + 38,
      { mark: PALETTE.status.good, tint: PALETTE.white }) + 2.5;
    chip(`${scored.filter((s) => s.health === 'at-risk').length} at risk`, chipX, y + 38,
      { mark: PALETTE.status.warning, tint: PALETTE.white });

    ring(RIGHT - 28, y + 25, 16, avgProgress, {
      weight: 5.5, track: PALETTE.white, value: `${avgProgress}%`, caption: 'avg progress', valueSize: 15
    });
    y += 58;

    sectionHeader('Every goal', 'bars');
    scored.forEach(({ goal, score, progress, health }, index) => {
      const state = HEALTH[health] || HEALTH['not-started'];
      space(22);
      card(PAGE.margin, y, CONTENT, 18, { fill: PALETTE.white, radius: 3, border: PALETTE.hair });

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9.6);
      setInk(PALETTE.ink);
      doc.text(sanitize(`${index + 1}.  ${goal.title || 'Untitled goal'}`),
        PAGE.margin + 6, y + 7, { maxWidth: CONTENT - 62 });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.6);
      setInk(PALETTE.muted);
      const meta = [goal.owner, goal.time_bound?.due_date ? `due ${prettyDate(goal.time_bound.due_date)}` : null]
        .filter(Boolean).join('   ·   ');
      if (meta) doc.text(sanitize(meta), PAGE.margin + 6, y + 12.5);

      meter(PAGE.margin + 6, y + 14.6, CONTENT - 62, progress, { height: 2.4 });

      chip(state.label, RIGHT - 52, y + 5.4, { mark: state.mark, tint: PALETTE.tint.slate, size: 7 });
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      setInk(PALETTE.ink);
      doc.text(String(score), RIGHT - 6, y + 12, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      setInk(PALETTE.muted);
      doc.text('SMART', RIGHT - 6, y + 15.6, { align: 'right' });

      y += 22;
    });
  };

  // ── assemble ──────────────────────────────────────────────────────────────

  if (goals.length === 0) {
    card(PAGE.margin, y, CONTENT, 34, { fill: PALETTE.tint.indigo, radius: 4.5 });
    icons.target(PAGE.margin + 9, y + 9, 4.5, PALETTE.ramp.fill);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    setInk(PALETTE.ink);
    doc.text(sanitize(docTitle), PAGE.margin + 9, y + 24, { maxWidth: CONTENT - 18 });
    y += 42;
  } else if (single) {
    goalSection(single);
  } else {
    portfolioCover();
    goals.forEach((goal) => {
      newPage();
      goalSection(goal);
    });
  }

  // ── the guide ─────────────────────────────────────────────────────────────

  if (includeGuide) {
    const guide = getSmartGoalGuide(language);
    if (goals.length > 0) newPage();

    card(PAGE.margin, y, CONTENT, 46, { fill: PALETTE.tint.lilac, radius: 4.5 });
    icons.bulb(PAGE.margin + 9, y + 8, 4.5, [126, 34, 206]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    setInk([126, 34, 206]);
    doc.text('APPENDIX', PAGE.margin + 16, y + 11.4, { charSpace: 0.4 });
    doc.setFontSize(17);
    setInk(PALETTE.ink);
    let headY = y + 22;
    doc.splitTextToSize(sanitize(guide.title), CONTENT - 18).slice(0, 2).forEach((line) => {
      doc.text(line, PAGE.margin + 9, headY);
      headY += 7.6;
    });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.4);
    setInk(PALETTE.body);
    doc.text(doc.splitTextToSize(sanitize(guide.subtitle), CONTENT - 18).slice(0, 2),
      PAGE.margin + 9, headY + 1.5);
    y += 54;

    guide.sections.forEach((section, index) => {
      space(28);
      setFill(PALETTE.ramp.wash);
      doc.circle(PAGE.margin + 3.6, y - 1.4, 3.6, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      setInk(PALETTE.ramp.deep);
      doc.text(String(index + 1), PAGE.margin + 3.6, y - 0.2, { align: 'center' });

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11.5);
      setInk(PALETTE.ink);
      // The number lives in the badge, so drop it from the heading text.
      const heading = sanitize(section.heading).replace(/^\d+\.\s*/, '');
      doc.splitTextToSize(heading, CONTENT - 12).forEach((line) => {
        doc.text(line, PAGE.margin + 10, y + 1.4);
        y += 6.4;
      });
      y += 2.5;

      if (section.body) paragraph(section.body, { x: PAGE.margin + 10, width: CONTENT - 10, gap: 3 });

      (section.points || []).forEach((point) => {
        space(15);
        setFill(PALETTE.ramp.fill);
        doc.circle(PAGE.margin + 11.6, y - 1.2, 0.9, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9.2);
        setInk(PALETTE.ink);
        doc.splitTextToSize(sanitize(point.title), CONTENT - 16).forEach((line) => {
          space(6);
          doc.text(line, PAGE.margin + 15, y);
          y += 5;
        });
        paragraph(point.text, { x: PAGE.margin + 15, width: CONTENT - 15, size: 9.2, leading: 4.9, gap: 3.5 });
      });

      if (section.example) {
        const textW = CONTENT - 24;
        const badLines = measure(section.example.bad, textW, 9.4, 'italic');
        const goodLines = measure(section.example.good, textW, 9.6, 'bold');
        const noteLines = (section.example.notes || [])
          .reduce((sum, note) => sum + measure(`- ${note}`, textW - 4, 8.6), 0);
        const height = 22 + badLines * 5 + goodLines * 5.3 + noteLines * 4.5;
        space(height + 6);

        card(PAGE.margin + 8, y, CONTENT - 8, height, { fill: PALETTE.tint.slate, radius: 3.5 });
        let boxY = y + 8;

        setFill(PALETTE.status.critical);
        doc.circle(PAGE.margin + 14.5, boxY - 1.4, 1.5, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6.8);
        setInk(PALETTE.muted);
        doc.text(sanitize(section.example.badLabel).toUpperCase(), PAGE.margin + 18, boxY, { charSpace: 0.3 });
        boxY += 5;
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(9.4);
        setInk(PALETTE.body);
        doc.splitTextToSize(sanitize(section.example.bad), textW).forEach((line) => {
          doc.text(line, PAGE.margin + 14, boxY);
          boxY += 5;
        });
        boxY += 4;

        setFill(PALETTE.status.good);
        doc.circle(PAGE.margin + 14.5, boxY - 1.4, 1.5, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6.8);
        setInk(PALETTE.muted);
        doc.text(sanitize(section.example.goodLabel).toUpperCase(), PAGE.margin + 18, boxY, { charSpace: 0.3 });
        boxY += 5;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9.6);
        setInk(PALETTE.ink);
        doc.splitTextToSize(sanitize(section.example.good), textW).forEach((line) => {
          doc.text(line, PAGE.margin + 14, boxY);
          boxY += 5.3;
        });
        boxY += 2;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.6);
        setInk(PALETTE.body);
        (section.example.notes || []).forEach((note) => {
          doc.splitTextToSize(sanitize(`- ${note}`), textW - 4).forEach((line) => {
            doc.text(line, PAGE.margin + 18, boxY);
            boxY += 4.5;
          });
        });

        y += height + 7;
      }

      y += 4;
    });
  }

  footer();

  if (returnDoc) return doc;

  const stamp = new Date().toISOString().split('T')[0];
  const safeName = sanitize(docTitle).replace(/[^\w\- ]+/g, '').trim().slice(0, 60) || 'SMART Goal';
  doc.save(fileName || `${safeName.replace(/\s+/g, '_')}_${stamp}.pdf`);
  return doc;
};

export const exportSingleSmartGoalToPDF = (goal, options = {}) =>
  exportSmartGoalsToPDF([goal], options);

export default exportSmartGoalsToPDF;
